import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import {
  invoiceReturnsApi,
  type InvoiceReturn,
  type InvoiceReturnOrder,
  type InvoiceReturnUpdate,
} from "@/lib/api";
import { integratedOrderErrorMessage } from "@/lib/integratedOrders";
import { invoiceReturnKindLabel, invoiceReturnReasonHint, invoiceReturnReasonLabel } from "@/lib/invoiceReturns";

interface InvoiceReturnEditSheetProps {
  item: InvoiceReturn | null;
  onClose: () => void;
  /** Chamado depois de salvar (e reprocessar, quando pedido) para a listagem recarregar. */
  onSaved: () => void;
}

interface OrderForm {
  salesId: string;
  salesStatus: string;
  fiscalDocumentNumber: string;
  fiscalDocumentStatus: string;
}

const text = (value?: string | null) => value ?? "";

const toOrderForm = (order: InvoiceReturnOrder): OrderForm => ({
  salesId: text(order.salesId),
  salesStatus: text(order.salesStatus),
  fiscalDocumentNumber: text(order.fiscalDocumentNumber),
  fiscalDocumentStatus: text(order.fiscalDocumentStatus),
});

const isValidJson = (value: string) => {
  try {
    JSON.parse(value);
    return true;
  } catch {
    return false;
  }
};

/**
 * Correção de uma chamada pendente do ERP, no mesmo padrão da correção dos pedidos integrados (painel lateral). "Salvar"
 * grava a correção e mantém a chamada pendente; "Salvar e reprocessar" também reaplica o corpo guardado pelo mesmo serviço
 * do ERP. Corpo ilegível só pode ser corrigido pelo JSON completo.
 */
export function InvoiceReturnEditSheet({ item, onClose, onSaved }: InvoiceReturnEditSheetProps) {
  const queryClient = useQueryClient();
  const { data: detail, isLoading, isError } = useQuery({
    queryKey: ["invoice-returns", "detail", item?.id],
    queryFn: () => invoiceReturnsApi.getDetail(item!.id),
    enabled: item != null,
  });

  const [preOrderReference, setPreOrderReference] = useState("");
  const [preOrderId, setPreOrderId] = useState("");
  const [salesId, setSalesId] = useState("");
  const [invoiceId, setInvoiceId] = useState("");
  const [accessKey, setAccessKey] = useState("");
  const [erpStatus, setErpStatus] = useState("");
  const [preOrderStatus, setPreOrderStatus] = useState("");
  const [orders, setOrders] = useState<OrderForm[]>([]);
  const [rawPayload, setRawPayload] = useState("");

  // Carrega o formulário com o corpo guardado (de novo quando a chamada é aberta ou corrigida).
  useEffect(() => {
    if (!detail) return;
    setPreOrderReference(text(detail.preOrderReference));
    setPreOrderId(text(detail.preOrderId));
    setSalesId(text(detail.salesId));
    setInvoiceId(text(detail.invoiceId));
    setAccessKey(text(detail.accessKey));
    setErpStatus(text(detail.erpStatus));
    setPreOrderStatus(text(detail.preOrderStatus));
    setOrders((detail.orders ?? []).map(toOrderForm));
    setRawPayload(text(detail.rawPayload));
  }, [detail]);

  const readable = detail?.payloadReadable ?? false;
  const isInvoice = detail?.kind === "invoice";

  const ordersChanged = useMemo(() => {
    const original = (detail?.orders ?? []).map(toOrderForm);
    return JSON.stringify(original) !== JSON.stringify(orders);
  }, [detail, orders]);

  // Só vai para a API o que mudou (campo ausente = não muda).
  const changes = useMemo<InvoiceReturnUpdate>(() => {
    if (!detail) return {};
    if (!detail.payloadReadable) {
      return rawPayload.trim() !== text(detail.rawPayload).trim() ? { payloadJson: rawPayload } : {};
    }

    const update: InvoiceReturnUpdate = {};
    const diff = (current: string, original?: string | null) => current !== text(original);
    if (diff(preOrderReference, detail.preOrderReference)) update.preOrderReference = preOrderReference;
    if (diff(preOrderId, detail.preOrderId)) update.preOrderId = preOrderId;
    if (diff(salesId, detail.salesId)) update.salesId = salesId;
    if (detail.kind === "invoice") {
      if (diff(invoiceId, detail.invoiceId)) update.invoiceId = invoiceId;
      if (diff(accessKey, detail.accessKey)) update.accessKey = accessKey;
    } else {
      if (diff(erpStatus, detail.erpStatus)) update.status = erpStatus;
      if (diff(preOrderStatus, detail.preOrderStatus)) update.preOrderStatus = preOrderStatus;
      if (ordersChanged) update.orders = orders;
    }
    return update;
  }, [detail, rawPayload, preOrderReference, preOrderId, salesId, invoiceId, accessKey, erpStatus, preOrderStatus, orders, ordersChanged]);

  const hasChanges = Object.keys(changes).length > 0;
  const jsonValid = readable || rawPayload.trim() === "" || isValidJson(rawPayload);
  const formValid = jsonValid && (readable || !hasChanges || rawPayload.trim() !== "");

  const save = useMutation({
    mutationFn: async (reprocess: boolean) => {
      if (!item) return null;
      if (hasChanges) await invoiceReturnsApi.update(item.id, changes);
      return reprocess ? invoiceReturnsApi.reprocess(item.id) : null;
    },
    onSuccess: (result, reprocess) => {
      if (reprocess && result) {
        if (result.resolved) toast.success("Chamada reprocessada e baixada.");
        else toast.warning(`Continua com pendência: ${invoiceReturnReasonLabel(result.reason)}`);
      } else {
        toast.success("Correção salva. A chamada continua pendente até ser reprocessada.");
      }
      queryClient.invalidateQueries({ queryKey: ["invoice-returns"] });
      onSaved();
      onClose();
    },
    onError: (error) => toast.error(integratedOrderErrorMessage(error)),
  });

  const updateOrder = (index: number, field: keyof OrderForm, value: string) =>
    setOrders((current) => current.map((order, i) => (i === index ? { ...order, [field]: value } : order)));

  const hint = invoiceReturnReasonHint(detail?.reason);

  return (
    <Sheet open={item != null} onOpenChange={(open) => !open && !save.isPending && onClose()}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-[720px]">
        <SheetHeader className="space-y-1 border-b px-6 py-5 pr-12 text-left sm:text-left">
          <SheetTitle className="text-xl font-bold leading-tight">Corrigir chamada {item?.id}</SheetTitle>
          <SheetDescription className="text-sm">
            {item ? `${invoiceReturnKindLabel(item.kind)} do ERP.` : ""} A correção mantém a chamada pendente; use
            "Salvar e reprocessar" para reaplicá-la agora, pelo mesmo serviço do ERP.
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 space-y-4 overflow-y-auto px-6 py-5">
          {isLoading ? (
            <div className="space-y-3"><Skeleton className="h-16" /><Skeleton className="h-16" /><Skeleton className="h-16" /></div>
          ) : isError || !detail ? (
            <p className="text-sm text-destructive">Não foi possível carregar a chamada.</p>
          ) : (
            <>
              <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm">
                <p className="font-medium text-destructive">{invoiceReturnReasonLabel(detail.reason)}</p>
                {detail.message && <p className="mt-1">{detail.message}</p>}
                {hint && <p className="mt-2 text-muted-foreground">O que fazer: {hint}</p>}
              </div>

              {readable ? (
                <>
                  <div className="grid gap-2">
                    <Label htmlFor="invoice-return-reference">Número de integração (preOrderReference)</Label>
                    <Input id="invoice-return-reference" value={preOrderReference} onChange={(event) => setPreOrderReference(event.target.value)} />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="invoice-return-pre-order">Pré-pedido (preOrderId, CNX-)</Label>
                    <Input id="invoice-return-pre-order" value={preOrderId} onChange={(event) => setPreOrderId(event.target.value)} />
                  </div>

                  {isInvoice ? (
                    <>
                      <div className="grid gap-2">
                        <Label htmlFor="invoice-return-invoice">Número da nota (invoiceId)</Label>
                        <Input id="invoice-return-invoice" value={invoiceId} onChange={(event) => setInvoiceId(event.target.value)} />
                        <p className="text-xs text-muted-foreground">
                          O ERP não envia o número: ele é lido do XML do arquivo
                          {detail.derivedInvoiceId ? ` (lido: ${detail.derivedInvoiceId})` : " (este arquivo não trouxe o número)"}.
                          Preencha só se precisar informar ou trocar o número, por exemplo quando o arquivo é um PDF.
                        </p>
                      </div>
                      <div className="grid gap-2">
                        <Label htmlFor="invoice-return-sales">OV (salesId)</Label>
                        <Input id="invoice-return-sales" value={salesId} onChange={(event) => setSalesId(event.target.value)} />
                      </div>
                      <div className="grid gap-2">
                        <Label htmlFor="invoice-return-key">Chave de acesso</Label>
                        <Input id="invoice-return-key" value={accessKey} onChange={(event) => setAccessKey(event.target.value)} />
                      </div>
                      {detail.files && detail.files.length > 0 && (
                        <p className="text-xs text-muted-foreground">
                          Arquivos guardados com a chamada (não editáveis): {detail.files.map((file) => (file.type ?? "?").toUpperCase()).join(", ")}.
                        </p>
                      )}
                    </>
                  ) : (
                    <>
                      <div className="grid gap-2">
                        <Label htmlFor="invoice-return-sales-status">OV (salesId)</Label>
                        <Input id="invoice-return-sales-status" value={salesId} onChange={(event) => setSalesId(event.target.value)} />
                      </div>
                      <div className="grid gap-2">
                        <Label htmlFor="invoice-return-erp-status">Status (nome do tipo, ex.: FaturadoERPDynamics)</Label>
                        <Input id="invoice-return-erp-status" value={erpStatus} onChange={(event) => setErpStatus(event.target.value)} />
                      </div>
                      <div className="grid gap-2">
                        <Label htmlFor="invoice-return-status">Status do pré-pedido (preOrderStatus)</Label>
                        <Input id="invoice-return-status" value={preOrderStatus} onChange={(event) => setPreOrderStatus(event.target.value)} />
                      </div>
                      <div className="grid gap-3">
                        <Label>OVs</Label>
                        {orders.length === 0 && (
                          <p className="text-xs text-muted-foreground">A chamada veio sem OVs (só o status do pré-pedido).</p>
                        )}
                        {orders.map((order, index) => (
                          <div key={index} className="grid gap-2 rounded-md border p-3 sm:grid-cols-2">
                            <div className="grid gap-1">
                              <Label htmlFor={`invoice-return-order-${index}-sales`} className="text-xs">OV (salesId)</Label>
                              <Input id={`invoice-return-order-${index}-sales`} value={order.salesId} onChange={(event) => updateOrder(index, "salesId", event.target.value)} />
                            </div>
                            <div className="grid gap-1">
                              <Label htmlFor={`invoice-return-order-${index}-status`} className="text-xs">Status da OV (salesStatus)</Label>
                              <Input id={`invoice-return-order-${index}-status`} value={order.salesStatus} onChange={(event) => updateOrder(index, "salesStatus", event.target.value)} />
                            </div>
                            <div className="grid gap-1">
                              <Label htmlFor={`invoice-return-order-${index}-fiscal`} className="text-xs">Nota fiscal (fiscalDocumentNumber)</Label>
                              <Input id={`invoice-return-order-${index}-fiscal`} value={order.fiscalDocumentNumber} onChange={(event) => updateOrder(index, "fiscalDocumentNumber", event.target.value)} />
                            </div>
                            <div className="grid gap-1">
                              <Label htmlFor={`invoice-return-order-${index}-fiscal-status`} className="text-xs">Situação da nota</Label>
                              <Input id={`invoice-return-order-${index}-fiscal-status`} value={order.fiscalDocumentStatus} onChange={(event) => updateOrder(index, "fiscalDocumentStatus", event.target.value)} />
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </>
              ) : (
                <div className="grid gap-2">
                  <Label htmlFor="invoice-return-raw">JSON completo da chamada</Label>
                  <p className="text-xs text-muted-foreground">
                    O corpo guardado está ilegível, então só pode ser corrigido pelo JSON completo (o mesmo formato que o ERP
                    envia). Se a chamada tinha arquivos em base64, eles fazem parte do texto.
                  </p>
                  <Textarea
                    id="invoice-return-raw"
                    rows={12}
                    className="font-mono text-xs"
                    value={rawPayload}
                    onChange={(event) => setRawPayload(event.target.value)}
                  />
                  {!jsonValid && <p className="text-xs text-destructive">O texto informado não é um JSON válido.</p>}
                </div>
              )}

              {detail.editedAt && (
                <p className="text-xs text-muted-foreground">
                  Última correção{detail.editedBy ? ` por ${detail.editedBy}` : ""}. Tentativas de reprocessamento: {detail.attempts}.
                </p>
              )}
            </>
          )}
        </div>

        <SheetFooter className="items-center gap-2 border-t px-6 py-4 sm:justify-end sm:space-x-0">
          <Button variant="outline" onClick={onClose} disabled={save.isPending}>Cancelar</Button>
          <Button variant="secondary" onClick={() => save.mutate(false)} disabled={save.isPending || !detail || !formValid || !hasChanges}>
            Salvar
          </Button>
          <Button onClick={() => save.mutate(true)} disabled={save.isPending || !detail || !formValid}>
            {save.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Salvar e reprocessar
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

